import { computeAbilityStats, orbitBlades, type GameState } from '@gra/engine';
import {
  AdditiveBlending,
  CanvasTexture,
  Color,
  ConeGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  type Object3D,
  OctahedronGeometry,
  PlaneGeometry,
  type Scene,
  Sprite,
  SpriteMaterial,
  Vector3,
} from 'three';
import { glow } from './fx';
import type { Particles } from './particles';
import { WORLD_SCALE as S } from './visuals';

const BLADE_HEIGHT = 1.0;

const bladeGeo = new OctahedronGeometry(0.3, 0).scale(2.6, 0.35, 0.9);
const totemPost = new CylinderGeometry(0.1, 0.15, 0.85, 6).translate(0, 0.42, 0);
const totemTip = new ConeGeometry(0.18, 0.32, 6).translate(0, 1.0, 0);
const discGeo = new PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
const woodMat = new MeshStandardMaterial({ color: '#5a3d24', roughness: 0.8 });
const ironMat = new MeshStandardMaterial({ color: '#9aa0a8', metalness: 0.8, roughness: 0.35 });

let ringTex: CanvasTexture | null = null;
/** Miękki pierścień z jaśniejszą krawędzią — tło aury na ziemi. */
function ringTexture(): CanvasTexture {
  if (ringTex) return ringTex;
  const c = document.createElement('canvas');
  c.width = c.height = 256;
  const ctx = c.getContext('2d')!;
  const g = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
  g.addColorStop(0, 'rgba(255,255,255,0)');
  g.addColorStop(0.55, 'rgba(255,255,255,0.08)');
  g.addColorStop(0.88, 'rgba(255,255,255,0.35)');
  g.addColorStop(0.96, 'rgba(255,255,255,0.9)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 256, 256);
  // Runiczne kreski na obwodzie: widać, że aura się obraca.
  ctx.strokeStyle = 'rgba(255,255,255,0.7)';
  ctx.lineWidth = 3;
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(128 + Math.cos(a) * 104, 128 + Math.sin(a) * 104);
    ctx.lineTo(128 + Math.cos(a + 0.18) * 112, 128 + Math.sin(a + 0.18) * 112);
    ctx.stroke();
  }
  ringTex = new CanvasTexture(c);
  return ringTex;
}

interface OrbitView {
  group: Group;
  blades: Group[];
  key: string;
}

interface AuraView {
  mesh: Mesh;
  mat: MeshBasicMaterial;
  key: string;
}

interface TrapView {
  root: Group;
  disc: Mesh;
  discMat: MeshBasicMaterial;
}

/**
 * Stałe efekty umiejętności, które nie są pojedynczym wybuchem: krążące ostrza, aury wokół bohatera,
 * pułapki na ziemi i znaki statusów na wrogach. Tylko czyta stan gry.
 */
export class AbilityViews {
  private readonly orbits = new Map<string, OrbitView>();
  private readonly auras = new Map<string, AuraView>();
  private readonly traps = new Map<number, TrapView>();
  private readonly tmp = new Vector3();
  private time = 0;

  constructor(
    private readonly scene: Scene,
    private readonly state: GameState,
    private readonly sparks: Particles,
    private readonly colorOf: (abilityId: string) => string,
  ) {}

  update(dt: number, hero: Object3D): void {
    this.time += dt;
    this.syncOrbits();
    this.syncAuras(hero);
    this.syncTraps();
    this.statusParticles();
  }

  private syncOrbits(): void {
    const seen = new Set<string>();
    for (const a of this.state.abilities) {
      const def = this.state.content.abilities[a.id];
      if (def?.kind !== 'orbit') continue;
      seen.add(a.id);
      const blades = orbitBlades(this.state, a);
      const color = this.colorOf(a.id);
      const key = `${blades.length}|${color}`;
      let view = this.orbits.get(a.id);
      if (!view || view.key !== key) {
        if (view) this.removeOrbit(view);
        view = this.createOrbit(blades.length, color, key);
        this.orbits.set(a.id, view);
      }
      blades.forEach((b, i) => {
        const blade = view!.blades[i]!;
        blade.position.set(b.x * S, BLADE_HEIGHT, b.y * S);
        // Ostrze ustawione stycznie do okręgu, czubkiem w kierunku obrotu.
        blade.rotation.y = -b.angle - Math.PI / 2;
        this.sparks.emit(blade.position, { color, count: 1, speed: 0.2, life: 0.25, size: 0.14, grow: 0.2, hot: true, drag: 3 });
      });
    }
    for (const [id, view] of this.orbits) {
      if (seen.has(id)) continue;
      this.removeOrbit(view);
      this.orbits.delete(id);
    }
  }

  private createOrbit(count: number, color: string, key: string): OrbitView {
    const c = new Color(color);
    const mat = new MeshStandardMaterial({ color: c, emissive: c, emissiveIntensity: 1.2, metalness: 0.6, roughness: 0.25 });
    const group = new Group();
    const blades: Group[] = [];
    for (let i = 0; i < count; i++) {
      const blade = new Group();
      blade.add(new Mesh(bladeGeo, mat));
      const halo = new Sprite(new SpriteMaterial({ map: glow(), color: c, blending: AdditiveBlending, depthWrite: false, opacity: 0.7, toneMapped: false }));
      halo.scale.setScalar(1.3);
      blade.add(halo);
      group.add(blade);
      blades.push(blade);
    }
    this.scene.add(group);
    return { group, blades, key };
  }

  private removeOrbit(view: OrbitView): void {
    view.group.removeFromParent();
    view.group.traverse((o) => {
      const m = (o as Mesh).material as MeshStandardMaterial | SpriteMaterial | undefined;
      m?.dispose();
    });
  }

  private syncAuras(hero: Object3D): void {
    const seen = new Set<string>();
    for (const a of this.state.abilities) {
      const def = this.state.content.abilities[a.id];
      if (def?.kind !== 'aura') continue;
      seen.add(a.id);
      const radius = computeAbilityStats(def, this.state.modifiers, a.rank).area * S;
      const color = this.colorOf(a.id);
      let view = this.auras.get(a.id);
      if (!view || view.key !== color) {
        if (view) this.removeAura(view);
        const mat = new MeshBasicMaterial({ map: ringTexture(), color: new Color(color), transparent: true, opacity: 0.55, blending: AdditiveBlending, depthWrite: false, toneMapped: false });
        view = { mesh: new Mesh(discGeo, mat), mat, key: color };
        this.scene.add(view.mesh);
        this.auras.set(a.id, view);
      }
      view.mesh.position.set(hero.position.x, 0.05, hero.position.z);
      view.mesh.scale.set(radius * 2, 1, radius * 2);
      view.mesh.rotation.y = this.time * 0.6;
      view.mat.opacity = 0.45 + 0.1 * Math.sin(this.time * 4);
      // Unoszące się drobinki (żar, szron) wewnątrz kręgu.
      if (Math.random() < 0.5) {
        const ang = Math.random() * Math.PI * 2;
        const r = Math.sqrt(Math.random()) * radius;
        this.tmp.set(hero.position.x + Math.cos(ang) * r, 0.15, hero.position.z + Math.sin(ang) * r);
        this.sparks.emit(this.tmp, { color, count: 1, speed: 0.2, life: 0.9, up: 1.4, size: 0.16, grow: 0.3, hot: def.status?.kind === 'burn', drag: 1 });
      }
    }
    for (const [id, view] of this.auras) {
      if (seen.has(id)) continue;
      this.removeAura(view);
      this.auras.delete(id);
    }
  }

  private removeAura(view: AuraView): void {
    view.mesh.removeFromParent();
    view.mat.dispose();
  }

  private syncTraps(): void {
    const seen = new Set<number>();
    for (const t of this.state.traps) {
      seen.add(t.id);
      let view = this.traps.get(t.id);
      if (!view) {
        const root = new Group();
        root.add(new Mesh(totemPost, woodMat), new Mesh(totemTip, ironMat));
        const discMat = new MeshBasicMaterial({ map: ringTexture(), color: new Color(this.colorOf(t.abilityId)), transparent: true, opacity: 0, blending: AdditiveBlending, depthWrite: false, toneMapped: false });
        const disc = new Mesh(discGeo, discMat);
        disc.position.y = 0.04;
        root.add(disc);
        this.scene.add(root);
        view = { root, disc, discMat };
        this.traps.set(t.id, view);
      }
      view.root.position.set(t.x * S, 0, t.y * S);
      // Krąg rośnie do zasięgu wybuchu, a po uzbrojeniu pulsuje szybciej.
      const armed = t.arm <= 0;
      const r = t.radius * S * (armed ? 1 : 0.4 + 0.6 * (1 - t.arm / 0.35));
      view.disc.scale.set(r * 2, 1, r * 2);
      view.disc.rotation.y = -this.time * 1.5;
      view.discMat.opacity = armed ? 0.35 + 0.25 * Math.sin(this.time * (t.life < 1.5 ? 24 : 8)) : 0.25;
    }
    for (const [id, view] of this.traps) {
      if (seen.has(id)) continue;
      view.root.removeFromParent();
      view.discMat.dispose();
      this.traps.delete(id);
    }
  }

  /** Podpalony wróg sypie żarem, spowolniony szronem, ogłuszony ma iskry nad głową. */
  private statusParticles(): void {
    for (const e of this.state.enemies) {
      if (e.burn && Math.random() < 0.35) {
        this.tmp.set(e.x * S + (Math.random() - 0.5) * 0.5, 0.8 + Math.random() * 0.8, e.y * S + (Math.random() - 0.5) * 0.5);
        this.sparks.emit(this.tmp, { color: '#ff7a2a', count: 1, speed: 0.3, life: 0.6, up: 2, size: 0.22, grow: 0.3, hot: true, drag: 1 });
      }
      if (e.slow && Math.random() < 0.15) {
        this.tmp.set(e.x * S + (Math.random() - 0.5) * 0.6, 0.4 + Math.random() * 1.2, e.y * S + (Math.random() - 0.5) * 0.6);
        this.sparks.emit(this.tmp, { color: '#bff0ff', count: 1, speed: 0.15, life: 0.8, up: -0.3, size: 0.13, grow: 0.5, drag: 1 });
      }
      if (e.stun && Math.random() < 0.4) {
        const a = this.time * 8 + e.id;
        this.tmp.set(e.x * S + Math.cos(a) * 0.35, 2.3, e.y * S + Math.sin(a) * 0.35);
        this.sparks.emit(this.tmp, { color: '#ffe36a', count: 1, speed: 0, life: 0.3, size: 0.18, grow: 0.4, hot: true });
      }
    }
  }

  dispose(): void {
    for (const v of this.orbits.values()) this.removeOrbit(v);
    for (const v of this.auras.values()) this.removeAura(v);
    for (const v of this.traps.values()) {
      v.root.removeFromParent();
      v.discMat.dispose();
    }
    this.orbits.clear();
    this.auras.clear();
    this.traps.clear();
  }
}
